import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import indexedDbFileStorage, {GIT_STATE, PREVIEW_STATE} from '../utils/indexedDbFileStorage';
import * as vfsClient from '../utils/vfsClient';
import {
  applyUnifiedDiff,
  parseUnifiedDiff
} from '../utils/diffParser';
import MonacoSearchPanel from './MonacoSearchPanel';
import DiffParserLoader from './DiffParserLoader';
import ConflictResolutionModal from './ConflictResolutionModal';
import useCollaboration from '../hooks/useCollaboration';
import { PresenceEditorBar } from './presence/Presence';
import ConflictPanel from './collab/ConflictPanel';
import EditorConnectionBanner, { useEditorConnection } from './collab/EditorConnectionBanner';
import useEditorCollab from '../hooks/useEditorCollab';
import CommentsPanel from './collab/CommentsPanel';
import FileAuthorChips from './workspace/FileAuthorChips';
import useCommentChips from '../hooks/useCommentChips';
import { BottomSheet } from './collab/BottomSheet';
import BlameSheet from './collab/BlameSheet';
import { useCompact, useElementWidth, useNarrowElement, usePhone } from '../hooks/useMediaQuery';
import { SuggestionEditor, useSuggestChangeRequests, useSuggestionRequests } from './collab/Suggestions';
import { splitVfsPath } from '../utils/presenceModel';
import { useAuth } from '../context/AuthContext';
import PipelineEditor from './PipelineEditor';
import MarkdownView from './markdown/MarkdownView';
import Icon from './icons/Icon';
import { getFileIconName } from './icons/fileIcons';
import { Button, Dropdown, Tooltip } from 'antd';
import {
  ArrowsInLineVertical, ArrowsOutLineVertical, ChatCircle, Check, ClockCounterClockwise, Copy, DotsThree, CornersIn, CornersOut, Eye, FloppyDisk, MagicWand,
  Moon, PencilSimple, Printer, Robot, Sun, Wrench, X,
} from '@phosphor-icons/react';
import './MonacoFileEditor.css';
import { devLog } from '../utils/devLog';

/** Text as HTML text (the print window's fallback). */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * MonacoFileEditor Component - VSCode-like file editor using Monaco Editor
 * Now supports real-time AI-driven content updates and localStorage persistence
 * @param {boolean} isPreviewMode - When true, enables auto-approve mode for seamless preview updates
 */
const MonacoFileEditor = ({file = null, onSave = null, readOnly = false, onContentChange = null, projectId = null, serviceName = null, isPreviewMode = false, onErrorsChange = null}) => {
  const [value, setValue] = useState('');
  // Mirrors `value` for handleGitPull below, which needs the CURRENT buffer
  // without adding `value` to its effect's deps (that would re-subscribe
  // the window listener on every keystroke).
  const valueRef = useRef('');
  useEffect(() => { valueRef.current = value; }, [value]);
  const [modified, setModified] = useState(false);
  const [gitState, setGitState] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  // Initialize theme based on app theme
  const getAppTheme = () => {
    const appTheme = localStorage.getItem('theme') || 'light';
    const isLightTheme = appTheme === 'light' || appTheme === 'monokai' || appTheme === 'catppuccin-latte' || appTheme === 'light-high-contrast';
    return isLightTheme ? 'vs' : 'vs-dark';
  };
  const [theme, setTheme] = useState(getAppTheme());
  const [isAIEditing, setIsAIEditing] = useState(false); // Track if AI is currently editing
  const [errorCount, setErrorCount] = useState(0); // Track error markers
  const [warningCount, setWarningCount] = useState(0); // Track warning markers
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const [showSearchPanel, setShowSearchPanel] = useState(false); // Track search panel visibility
  const [showDiffLoader, setShowDiffLoader] = useState(false); // Track diff parser loader
  // A Markdown file opens on its rendered preview (a deep link `?view=files&path=README.md` too); "Source" shows the
  // editor to change it. Each newly opened file starts that way again; a link to one of its lines (`&line=42`, a search
  // result) opens the source on that line.
  const opensRendered = /\.(md|markdown)$/i.test(file?.name || file?.path || '') && !file?.lineNumber;
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(opensRendered); // Track markdown preview mode
  useEffect(() => { setShowMarkdownPreview(opensRendered); }, [file?.path, opensRendered]);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false); // Track HTML preview mode
  const [showSvgPreview, setShowSvgPreview] = useState(false); // Track SVG preview mode
  const [showPipelineEditor, setShowPipelineEditor] = useState(false); // Track pipeline editor mode
  const [diffLoaderFileName, setDiffLoaderFileName] = useState(''); // Track file being processed
  const [diffProgressInfo, setDiffProgressInfo] = useState(null); // Track diff progress details
  const [foldingState, setFoldingState] = useState('expanded'); // Track folding state
  
  // Conflict resolution modal state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [failedFiles, setFailedFiles] = useState([]);
  
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  // Bumped when Monaco mounts, so hooks that need the editor instance (useEditorCollab) see it.
  const [editorMounted, setEditorMounted] = useState(0);
  // Phones and tablets in portrait: no minimap, narrower gutters, larger touch targets (MonacoFileEditor.css).
  const containerRef = useRef(null);
  const narrowEditor = useNarrowElement(containerRef, 560);
  // The header's own width: below ~600px the view buttons fold into "⋯", below ~420px Blame and Comments too.
  const headerRef = useRef(null);
  const headerWidth = useElementWidth(headerRef);
  const headerFold = !headerWidth || headerWidth >= 600 ? 0 : headerWidth >= 420 ? 1 : 2;
  const compactWindow = useCompact();
  const isCompact = compactWindow || narrowEditor;
  const isPhone = usePhone();
  useEffect(() => {
    const editor = editorMounted ? editorRef.current : null;
    if (!editor) { return; }
    try {
      editor.updateOptions(isCompact
        ? { minimap: { enabled: false }, folding: false, lineNumbersMinChars: 3, lineDecorationsWidth: 8, fontSize: isPhone ? 13 : 12, lineHeight: isPhone ? 22 : 19 }
        : { minimap: { enabled: true }, folding: true, lineNumbersMinChars: 5, lineDecorationsWidth: 10, fontSize: 12, lineHeight: 19 });
    } catch { /* disposed */ }
  }, [editorMounted, isCompact, isPhone]);
  const debounceTimerRef = useRef(null); // Debounce timer for expensive operations
  // Guards against a self-echo loop with vfs-service: model.setValue() (used by
  // setFileContent, below) fires Monaco's onChange synchronously just like a real
  // keystroke would, and handleChange has no other way to tell "the user typed
  // this" apart from "we just overwrote the buffer with a fresh sync". Without
  // this, handleGitPull's own refresh gets treated as a user edit, queues its
  // own debounced saveFile (compared against the stale `file.content` prop, so
  // isModified is wrongly true), and — if it lands mid-typing rather than on an
  // idle buffer — can push a stale/partial version back over vfs-service's own
  // update. Same purpose as useCollaboration's isApplyingRemoteUpdate, for the
  // non-collaboration sync path.
  const isApplyingExternalUpdateRef = useRef(false);
  const currentFilePathRef = useRef(null); // Track current file path to prevent unnecessary reloads
  const lastScrolledLineRef = useRef(null); // Track last scrolled line to detect changes
  // `${projectId}:${serviceName}` pairs already verified fresh against
  // vfs-service THIS component instance's lifetime — see loadFileContent's
  // own comment on why this exists.
  const verifiedVfsServicesRef = useRef(new Set());

  // Helper to load file content without polluting the undo stack.
  // @monaco-editor/react controlled mode applies value-prop changes via executeEdits(),
  // which creates undo entries. When loading a new file the editor briefly holds ''
  // (initial useState), so the '' → file-content transition lands in the undo history.
  // Pressing CMD/CTRL+Z then undoes that transition, emptying the editor.
  // Fix: set the content directly on the Monaco model first (model.setValue clears the
  // undo stack), then update React state. Because the model already holds the new value
  // by the time @monaco-editor/react's effect runs, it finds getValue() === value and
  // skips its executeEdits() call, leaving the undo stack clean.
  const setFileContent = (content) => {
    editorRef.current?.getModel()?.setValue(content);
    setValue(content);
  };

  // Live editing of a vfs-backed project's files (useCollaboration.js), on by default: one shared doc per file, the
  // others' cursors, and the project's presence (who is here, which AI agent writes). `enableCollaboration` set to
  // 'false' in localStorage turns it off in this browser.
  const collaborationEnabled = Boolean(localStorage.getItem('enableCollaboration') !== 'false' && projectId && serviceName && file
    && !file.isBinary && vfsClient.isVfsBackedProject(projectId));

  const collab = useCollaboration(
    collaborationEnabled ? editorRef.current : null,
    collaborationEnabled ? monacoRef.current : null,
    collaborationEnabled ? file : null,
    collaborationEnabled ? projectId : null,
    collaborationEnabled ? serviceName : null
  );
  // The file's vfs path. The project's own Files view (Home) opens files with the service "." (the project root): its
  // paths are already from the root, so "./services/web/a.js" is "services/web/a.js" (vfs-service refuses the "./").
  const presenceRelPath = collaborationEnabled ? `${serviceName}/${file.path || file.name}`.replace(/^(\.\/)+/, '').replace(/\/{2,}/g, '/') : null;
  // The Markdown preview's file in the project (its relative links and images resolve from its folder); a link to
  // another project file opens it here, in Code, as a comment on another file does.
  const serviceRoot = serviceName && serviceName !== '.' ? String(serviceName).replace(/^\.\/+|\/+$/g, '') : '';
  const markdownFilePath = [serviceRoot, String(file?.path || file?.name || '').replace(/^\.?\/+/, '')].filter(Boolean).join('/');
  const openLinkedFile = useCallback((path) => {
    const inService = serviceRoot && path.startsWith(`${serviceRoot}/`);
    const {service, file: otherFile} = inService ? {service: serviceRoot, file: path.slice(serviceRoot.length + 1)} : splitVfsPath(path);
    window.dispatchEvent(new CustomEvent('openFile', {detail: {projectId, serviceName: service || undefined, filePath: otherFile}}));
  }, [projectId, serviceRoot]);
  // A viewer of a shared project reads only; the editor also waits for the live doc before taking keystrokes.
  const isPresenceViewer = collaborationEnabled && collab.presence?.you?.role === 'viewer';
  const editorReadOnly = readOnly || isPresenceViewer || (collaborationEnabled && collab.status === 'connecting');
  // Collaboration phase 2 (useEditorCollab): follow scrolling, blame, comment markers and panel.
  const {user: authUser} = useAuth();
  const editorCollab = useEditorCollab({
    editor: collaborationEnabled && editorMounted ? editorRef.current : null,
    monaco: collaborationEnabled && editorMounted ? monacoRef.current : null,
    projectId: collaborationEnabled ? projectId : null,
    path: presenceRelPath,
    presence: collab.presence,
    touch: isPhone,
  });
  // Not connected to Flow (offline, session expired): the text may be an older copy, so a banner says so and the
  // editor reads only until it is connected again (collab/EditorConnectionBanner.jsx).
  const connection = useEditorConnection(collaborationEnabled ? collab.status : 'off');
  // Inline "SC · 1 comment" chips at the end of commented lines; a click opens the comments panel on that thread.
  useCommentChips({
    editor: collaborationEnabled && editorMounted ? editorRef.current : null,
    monaco: collaborationEnabled && editorMounted ? monacoRef.current : null,
    threads: editorCollab.threads,
    onOpen: (threadId) => { editorCollab.setActiveThreadId(threadId); editorCollab.setCommentsOpen(true); },
  });
  // Phones: Blame and Comments are bottom sheets (a tap on a line number with blame on opens that line's block).
  const [blameSheetOpen, setBlameSheetOpen] = useState(false);
  useEffect(() => {
    if (isPhone && editorCollab.blameLine) { setBlameSheetOpen(true); }
  }, [isPhone, editorCollab.blameLine]);
  const onBlameButton = () => {
    if (!isPhone) { editorCollab.toggleBlame(); return; }
    if (!editorCollab.blameOn) { editorCollab.setBlameOn(true); }
    editorCollab.setBlameLine(null);
    setBlameSheetOpen(true);
  };
  const goToLine = (line) => {
    if (editorRef.current && Number.isInteger(line)) {
      editorRef.current.revealLineInCenter(line);
      editorRef.current.setPosition({lineNumber: line, column: 1});
    }
  };
  // Viewer suggestions (collaboration phase 3): a viewer's attempt to type opens "Suggest a change", a separate editor
  // seeded with the file as last committed; the live doc is never touched.
  const [suggesting, setSuggesting] = useState(false);
  useEffect(() => {
    const editor = editorMounted ? editorRef.current : null;
    if (!editor || !isPresenceViewer || typeof editor.onDidAttemptReadOnlyEdit !== 'function') { return undefined; }
    const sub = editor.onDidAttemptReadOnlyEdit(() => setSuggesting(true));
    return () => sub.dispose();
  }, [editorMounted, isPresenceViewer]);
  // The side panel's tab (This file / Project comments, or this file's Suggestions) and the suggestion it shows: Open in
  // Code from the Suggestions dialog opens it here; "Suggest a change" there opens this file's when you only view it.
  const [sidePanelScope, setSidePanelScope] = useState('file');
  const [focusSuggestionId, setFocusSuggestionId] = useState(null);
  const [suggestionsReloadKey, setSuggestionsReloadKey] = useState(0);
  useSuggestionRequests({
    projectId: collaborationEnabled ? projectId : null,
    path: presenceRelPath,
    onOpen: (suggestionId) => {
      setSidePanelScope('suggestions');
      setFocusSuggestionId(suggestionId);
      setSuggestionsReloadKey((key) => key + 1);
      editorCollab.setCommentsOpen(true);
    },
  });
  useSuggestChangeRequests({ projectId, enabled: collaborationEnabled && isPresenceViewer, onSuggest: () => setSuggesting(true) });
  const goToCommentLine = (thread) => {
    if (thread.path === presenceRelPath) {
      if (editorRef.current && Number.isInteger(thread.line)) {
        editorRef.current.revealLineInCenter(thread.line);
        editorRef.current.setPosition({lineNumber: thread.line, column: 1});
      }
      editorCollab.setActiveThreadId(thread.id);
      return;
    }
    const {service, file: otherFile} = splitVfsPath(thread.path);
    window.dispatchEvent(new CustomEvent('openFile', {
      detail: {projectId, serviceName: service || undefined, filePath: otherFile, lineNumber: thread.line || undefined},
    }));
  };
  // Bound to the live doc, the file is saved by vfs-service (per author): the editor's own saves stay local. Not
  // connected, they stay local too: the text may be an older copy, never written over the newer file.
  const liveSave = () => (collab.isLive() || connection.blocked ? {liveSynced: true} : {});

  // Watch for app theme changes and update Monaco theme
  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = getAppTheme();
      setTheme(newTheme);
    };

    // Listen for storage events (theme changes)
    window.addEventListener('storage', handleThemeChange);
    // Custom event for same-tab theme changes
    window.addEventListener('themechange', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  // Parse diff-style content and merge into existing editor content







  // Manual review mode removed - all changes auto-applied via external diff parser

  // Detect language from file extension
  const detectLanguage = (filename) => {
    if (!filename) return 'plaintext';
    
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap = {
      // JavaScript/TypeScript
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      mjs: 'javascript',
      cjs: 'javascript',
      
      // Web
      html: 'html',
      htm: 'html',
      css: 'css',
      scss: 'scss',
      sass: 'sass',
      less: 'less',
      
      // Data formats
      json: 'json',
      yaml: 'yaml',
      yml: 'yaml',
      xml: 'xml',
      svg: 'xml',
      toml: 'toml',
      
      // Markdown & Docs
      md: 'markdown',
      mdx: 'markdown',
      txt: 'plaintext',
      
      // Programming languages
      py: 'python',
      java: 'java',
      c: 'c',
      cpp: 'cpp',
      cs: 'csharp',
      go: 'go',
      rs: 'rust',
      rb: 'ruby',
      php: 'php',
      swift: 'swift',
      kt: 'kotlin',
      scala: 'scala',
      
      // Shell & Config
      sh: 'shell',
      bash: 'shell',
      zsh: 'shell',
      fish: 'shell',
      dockerfile: 'dockerfile',
      
      // SQL
      sql: 'sql',
      
      // Other
      graphql: 'graphql',
      proto: 'protobuf',
      r: 'r',
      lua: 'lua'
    };
    
    // Check for special filenames
    const specialFiles = {
      'dockerfile': 'dockerfile',
      'docker-compose.yml': 'yaml',
      'docker-compose.yaml': 'yaml',
      'makefile': 'makefile',
      '.gitignore': 'plaintext',
      '.env': 'plaintext',
      'package.json': 'json',
      'tsconfig.json': 'json',
      'jsconfig.json': 'json',
      // Helm chart special files — use yaml as base language
      'chart.yaml': 'yaml',
      'chart.lock': 'yaml',
      'values.yaml': 'yaml',
      'values.schema.json': 'json',
      '_helpers.tpl': 'yaml',
      'notes.txt': 'plaintext',
    };

    // Helm template files (*.tpl, and files under templates/ or crds/)
    const lowerFilename = filename.toLowerCase();
    const isHelmTemplate = lowerFilename.endsWith('.tpl') ||
      lowerFilename.includes('templates/') ||
      lowerFilename.includes('crds/');
    if (isHelmTemplate) {
      return 'yaml';
    }

    if (specialFiles[lowerFilename]) {
      return specialFiles[lowerFilename];
    }
    
    return languageMap[ext] || 'plaintext';
  };

  /**
   * Handle user confirming manual resolution of conflict
   */
  const handleManualResolve = useCallback(() => {
    setShowConflictModal(false);
    
    if (!conflictData) return;
    
    const { targetPath } = conflictData;
    
    // Show a notification to the user about manual resolution
    devLog(`📝 User will manually resolve conflict for ${targetPath}`);
    
    // Dispatch event to notify that manual resolution is needed
    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
      detail: {
        projectId: conflictData.targetProjectId,
        serviceName: conflictData.targetServiceName,
        filePath: targetPath,
        error: conflictData.error,
        manualResolutionRequested: true
      }
    }));
    
    // Clear state
    setConflictData(null);
    setFailedFiles([]);
    setShowDiffLoader(false);
  }, [conflictData]);
  
  /**
   * Collapse all code sections
   */
  const collapseAll = useCallback(() => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    editor.trigger('fold', 'editor.foldAll', {});
    setFoldingState('collapsed');
  }, []);

  /**
   * Expand all code sections
   */
  const expandAll = useCallback(() => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    editor.trigger('unfold', 'editor.unfoldAll', {});
    setFoldingState('expanded');
  }, []);

  /**
   * Handle user canceling conflict resolution
   */
  const handleConflictCancel = useCallback(() => {
    setShowConflictModal(false);
    
    if (!conflictData) return;
    
    const { targetPath, targetProjectId, targetServiceName } = conflictData;
    
    devLog(`❌ User canceled conflict resolution for ${targetPath}`);
    
    // Dispatch event to notify that diff failed and was canceled
    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
      detail: {
        projectId: targetProjectId,
        serviceName: targetServiceName,
        filePath: targetPath,
        error: conflictData.error,
        userCanceled: true
      }
    }));
    
    // Clear state
    setConflictData(null);
    setFailedFiles([]);
    setShowDiffLoader(false);
  }, [conflictData]);

  /**
   * Apply diff to a non-open file in the background
   * Loads file content from IndexedDB, applies diff, saves back to IndexedDB
   */
  const applyDiffInBackground = useCallback(async (diffContent, targetPath, targetProjectId, targetServiceName) => {
    try {
      // Show diff parser loader
      const fileName = targetPath.split('/').pop();
      setDiffLoaderFileName(fileName);
      setDiffProgressInfo({
        stage: 'Initializing',
        operation: 'Parsing diff',
        llmActive: false
      });
      setShowDiffLoader(true);
      
      // Dispatch loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: true, message: `AI writing to ${fileName}...`}
      }));

      // eslint-disable-next-line no-console
      devLog(`🔧 Applying diff in background for: ${targetPath}`);
      
      // Parse the diff to get operation info
      setDiffProgressInfo(prev => ({...prev, stage: 'Parsing', operation: 'Analyzing diff format'}));
      const parsed = parseUnifiedDiff(diffContent);
      
      if (!parsed.isValid) {
        // eslint-disable-next-line no-console
        console.error(`❌ Invalid diff for ${targetPath}:`, parsed.error);
        return;
      }
      
      // Handle file deletion
      if (parsed.isFileDeletion) {
        // eslint-disable-next-line no-console
        devLog(`🗑️ Deleting file from IndexedDB: ${targetPath}`);
        setDiffProgressInfo(prev => ({...prev, stage: 'Deleting', operation: 'Removing file'}));
        
        await indexedDbFileStorage.deleteFile(targetProjectId, targetServiceName, targetPath);
        
        // Dispatch completion event with file info
        window.dispatchEvent(new CustomEvent('diffParserComplete', {
          detail: {
            filePath: targetPath,
            operation: 'delete',
            projectId: targetProjectId,
            serviceName: targetServiceName,
            files: [{
              path: targetPath,
              operation: 'delete',
              projectId: targetProjectId,
              serviceName: targetServiceName
            }]
          }
        }));
        
        // Show success notification
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: `File deleted: ${fileName}`,
            duration: 3000
          }
        }));
        
        // eslint-disable-next-line no-console
        devLog(`✅ File deleted from IndexedDB: ${targetPath}`);
        return;
      }
      
      // Get current content from IndexedDB only
      setDiffProgressInfo(prev => ({...prev, stage: 'Loading', operation: 'Reading file content'}));
      let currentContent = '';
      
      const stored = await indexedDbFileStorage.getFile(targetProjectId, targetServiceName, targetPath);
      if (stored && stored.content) {
        currentContent = stored.content;
        // eslint-disable-next-line no-console
        devLog(`📂 Loaded ${targetPath} from IndexedDB for background diff`);
      } else {
        // No stored content - assume empty (new file or not yet loaded)
        // eslint-disable-next-line no-console
        devLog(`🆕 No stored content for ${targetPath}, using empty base`);
        currentContent = '';
      }
      
      // Apply the diff with progress tracking
      setDiffProgressInfo(prev => ({...prev, stage: 'Applying', operation: 'Merging changes'}));
      const result = await applyUnifiedDiff(diffContent, currentContent, {
        onProgress: (progress) => {
          // Update progress info with detailed stage information
          setDiffProgressInfo({
            stage: progress.stage || 'Processing',
            operation: progress.message || progress.text || 'Applying changes',
            confidence: progress.confidence || null
          });
        }
      });
      
      // Check if diff application succeeded
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.error(`❌ Failed to apply diff to ${targetPath}:`, result.error);
        // eslint-disable-next-line no-console
        console.error(`   Diff content length: ${diffContent.length} chars`);
        // eslint-disable-next-line no-console
        console.error(`   Current content length: ${currentContent.length} chars`);
        
        // Hide loader on failure
        setShowDiffLoader(false);
        
        // Show conflict resolution modal for user to handle
        setFailedFiles([targetPath]);
        setConflictData({
          diffContent,
          currentContent,
          targetPath,
          targetProjectId,
          targetServiceName,
          error: result.error
        });
        setShowConflictModal(true);
        
        // Dispatch event to notify that diff failed
        window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
          detail: {
            projectId: targetProjectId,
            serviceName: targetServiceName,
            filePath: targetPath,
            fileName: fileName,
            currentContent: currentContent,
            failedDiff: diffContent,
            error: result.error,
            isBackgroundDiff: true
          }
        }));
        
        return; // Exit early on failure
      }
      
      // Save the result to localStorage
      // fileName already declared at the start of this function
      setDiffProgressInfo(prev => ({...prev, stage: 'Saving', operation: 'Writing to storage', llmActive: false}));
      
      // Check if file is new (doesn't exist in IndexedDB or has no original content)
      const isNewFile = !stored || !stored.originalContent;
      
      // CRITICAL: Preserve the original content when applying diff
      // If file exists in IndexedDB, keep its original content
      // If new file, use currentContent (which is empty or initial state) as original
      const originalContentToSave = stored?.originalContent || currentContent;
      
      await indexedDbFileStorage.saveFile(
        targetProjectId,
        targetServiceName,
        targetPath,
        result.newContent,
        {
          originalContent: originalContentToSave,
          fileName: fileName,
          appliedInBackground: true,
          isNew: isNewFile
        }
      );
      
      // Update file state
      
      const currentState = await indexedDbFileStorage.getFileState(targetProjectId, targetServiceName, targetPath);
      
      // If this is a new file, dispatch fileOperation event to update tree
      if (isNewFile) {
        window.dispatchEvent(new CustomEvent('fileOperation', {
          detail: {
            operation: 'create',
            path: targetPath,
            content: result.newContent
          }
        }));
        
        // FIX #1: For NEW files, trigger immediate sync (no debounce)
        window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
          detail: {
            projectId: targetProjectId,
            serviceName: targetServiceName,
            filePath: targetPath
          }
        }));
      }
      
      // Dispatch completion event for loader with file info
      window.dispatchEvent(new CustomEvent('diffParserComplete', {
        detail: {
          filePath: targetPath,
          operation: currentState === 'new' ? 'create' : 'update',
          projectId: targetProjectId,
          serviceName: targetServiceName,
          files: [{
            path: targetPath,
            operation: currentState === 'new' ? 'create' : 'update',
            projectId: targetProjectId,
            serviceName: targetServiceName
          }]
        }
      }));
      
      // Dispatch custom event for auto-sync and tree update
      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
        detail: {
          projectId: targetProjectId,
          serviceName: targetServiceName,
          filePath: targetPath
        }
      }));
      
      // eslint-disable-next-line no-console
      devLog(`✅ Background diff applied and saved to localStorage: ${targetPath} (state: ${currentState})`);
      
      // Show success notification to user
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'success',
          message: `File updated in background: ${fileName}`,
          duration: 3000
        }
      }));
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`❌ Error applying diff in background for ${targetPath}:`, error);
      
      // Hide loader on error
      setShowDiffLoader(false);
      
      // Show error notification
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: `Failed to update ${targetPath}: ${error.message}`,
          duration: 5000
        }
      }));
    } finally {
      // Clear loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: false, message: ''}
      }));
    }
  }, []);

  // Update value when file changes - check IndexedDB first
  useEffect(() => {
    const loadFileContent = async () => {
      // eslint-disable-next-line no-console
      devLog('📋 MonacoFileEditor received file:', {
        path: file?.path,
        isBinary: file?.isBinary,
        mimeType: file?.mimeType,
        hasBinaryUrl: !!file?.binaryUrl,
        binaryUrl: file?.binaryUrl?.substring(0, 50)
      });
      
      if (file?.content !== undefined && projectId && serviceName && file?.path) {
        // CRITICAL FIX: Only reload from IndexedDB when file path actually changes
        // This prevents losing focus when user is typing
        const hasFileChanged = currentFilePathRef.current !== file.path;
        
        if (!hasFileChanged) {
          // User is still on the same file - don't reload from IndexedDB
          // This prevents interrupting the user's typing
          return;
        }
        
        // File has changed - update the ref and load from IndexedDB
        currentFilePathRef.current = file.path;
        
        // Dispatch loading event
        window.dispatchEvent(new CustomEvent('indexedDBLoading', {
          detail: {loading: true, message: `Loading ${file.path.split('/').pop()} from IndexedDB...`}
        }));

        // Check if there are local modifications in IndexedDB
        let stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);

        // BUG FIX: IndexedDB is trusted unconditionally below whenever it
        // has ANY entry for this file — correct for preserving a real
        // local edit, but wrong for a STALE entry (e.g. the default
        // scaffold's placeholder App.tsx, written at project-creation
        // time, before the agent ever wrote its real code). This tab only
        // learns about the agent's real writes via Canvas.jsx's WS
        // live-sync effect, which starts cold the moment Code mode mounts
        // (Canvas isn't rendered at all on the plain /chat door) and needs
        // its own network round-trip to finish reconciling IndexedDB — a
        // real race against this file-open path, which reads whatever's
        // ALREADY in IndexedDB right now. Live-confirmed: switching from
        // Chat to Code right after the agent finished a build showed the
        // scaffold placeholder instead of the real generated file. Close
        // that race here, directly, once per (project, service, file) per
        // editor session — not by trusting the live-sync effect's timing,
        // since that's a separate, best-effort background concern (see its
        // own comment on ownership) this file-open path shouldn't depend on.
        //
        // BUG FIX (live-reported: App.tsx stayed scaffold forever after
        // main.tsx alone got verified): this key used to be scoped to just
        // `${projectId}:${serviceName}` — the FIRST file opened per service
        // "used up" the check for every other file in that same service for
        // the rest of the session, since the Set entry only proves ONE file
        // was verified, not the whole service. Scoping the key to the file
        // path too makes every distinct file get its own one-shot check.
        const verificationKey = `${projectId}:${serviceName}:${file.path}`;
        if (
          vfsClient.isVfsBackedProject(projectId) &&
          !verifiedVfsServicesRef.current.has(verificationKey)
        ) {
          verifiedVfsServicesRef.current.add(verificationKey);
          try {
            const authoritative = await vfsClient.readFile(projectId, `${serviceName}/${file.path}`);
            if (typeof authoritative === 'string' && authoritative !== stored?.content) {
              // eslint-disable-next-line no-console
              devLog('🔍 MonacoFileEditor: IndexedDB was stale for', file.path, '— reconciling from vfs-service');
              await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, authoritative, { fromVfs: true });
              stored = { content: authoritative };
            }
          } catch (error) {
            // Best-effort verification — a failed read here (network blip,
            // file genuinely doesn't exist yet) falls back to whatever
            // IndexedDB already had, exactly like before this fix.
            // eslint-disable-next-line no-console
            console.warn('⚠️ MonacoFileEditor: vfs-service freshness check failed for', file.path, error);
          }
        }

        const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);

        // BUG FIX (data corruption, not just staleness): every
        // setFileContent() call below used to run UNGUARDED — unlike the
        // gitPullCompleted handler's own identical-shaped update, which
        // correctly wraps its setFileContent() in
        // isApplyingExternalUpdateRef so handleChange's onChange callback
        // knows to ignore it. Without that guard here, Monaco's onChange
        // fires for this purely PROGRAMMATIC initial load exactly as if
        // the user had typed it, and handleChange's 500ms-debounced
        // autosave then calls indexedDbFileStorage.saveFile(...) with NO
        // fromVfs/fromGit flag — which indexedDbFileStorage.js treats as a
        // genuine local edit and PUSHES IT UP to vfs-service via
        // patchFile/writeFile. Combined with the stale-placeholder race
        // this function's freshness check above closes for the FIRST file
        // opened, this meant opening ANY file whose IndexedDB copy still
        // held old content (the scaffold placeholder, or anything not yet
        // caught by the freshness check) could silently overwrite the
        // agent's real, already-committed file on vfs-service with that
        // stale content — live-confirmed: the "authoritative" vfs-service
        // read itself came back as the placeholder on a later reload, i.e.
        // actual data loss on the source of truth, not just a display bug.
        // Guarded the same way the git-pull path already does.
        isApplyingExternalUpdateRef.current = true;
        try {
          if (stored && stored.content) {
            // Validate stored content before using it
            const storedLooksInvalid = stored.content.includes('npm install') &&
              stored.content.includes('yarn add') &&
              !stored.content.includes('import');

            if (storedLooksInvalid) {
              // eslint-disable-next-line no-console
              console.error('❌ CRITICAL: localStorage contains invalid content for', file.path);
              // eslint-disable-next-line no-console
              console.error('   Deleting corrupted localStorage entry and using server content');
              await indexedDbFileStorage.deleteFile(projectId, serviceName, file.path);
              setFileContent(file.content);
              setModified(false);
              setGitState(null);
              setPreviewState(null);
            } else {
              // Use locally stored content
              setFileContent(stored.content);
              setModified(stored.content !== file.content);
              setGitState(currentState?.gitState || null);
              setPreviewState(currentState?.previewState || null);
              // eslint-disable-next-line no-console
              devLog('📂 Loaded file from IndexedDB:', file.path,
                'GitState:', currentState?.gitState,
                'PreviewState:', currentState?.previewState,
                'Content length:', stored.content.length,
                'Lines:', stored.content.split('\n').length,
                'Modified:', stored.content !== file.content);
            }
          } else {
            // Use server content
            setFileContent(file.content);
            setModified(false);
            setGitState(null);
            setPreviewState(null);
          }
        } finally {
          isApplyingExternalUpdateRef.current = false;
        }

        // Clear loading event
        window.dispatchEvent(new CustomEvent('indexedDBLoading', {
          detail: {loading: false, message: ''}
        }));
      } else if (file?.content !== undefined) {
        // Fallback when projectId/serviceName not provided — same guard,
        // same reasoning: this is a programmatic load, never a user edit.
        isApplyingExternalUpdateRef.current = true;
        setFileContent(file.content);
        isApplyingExternalUpdateRef.current = false;
        setModified(false);
        setGitState(null);
        setPreviewState(null);
        currentFilePathRef.current = file?.path || null;
      }
      
    };
    
    loadFileContent();
  }, [file, projectId, serviceName]);

  // The line a link or a search result asked for (`#L42`, `&line=42`): revealed in the center, the cursor on it, and
  // highlighted for a moment. Retried until this file's editor is mounted and holds its text: Monaco mounts after the
  // file is chosen (it is loaded on first use) and the text arrives after that, so a single try on a timer used to find
  // no editor, or an empty model, and the file stayed on line 1.
  const lineFlashRef = useRef([]);
  useEffect(() => {
    const lineNumber = file?.lineNumber;
    const filePath = file?.path;
    if (!lineNumber) { return undefined; }
    const lineKey = `${filePath}:${lineNumber}`;
    if (lastScrolledLineRef.current === lineKey) { return undefined; }
    let cancelled = false;
    let timer = null;
    let tries = 0;
    const reveal = () => {
      if (cancelled) { return; }
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      let model = null;
      try { model = editor?.getModel?.() || null; } catch { model = null; }
      const lineCount = model ? model.getLineCount() : 0;
      // Not mounted yet, or the text not in yet (a shorter model is given ~3 s before the line is clamped to the end).
      if (!editor || !monaco || !model || (lineCount < lineNumber && tries < 40)) {
        tries += 1;
        timer = setTimeout(reveal, 75);
        return;
      }
      const target = Math.min(Math.max(1, lineNumber), lineCount);
      devLog('📍 Revealing line', target, 'of', lineCount, 'in', filePath);
      editor.revealLineInCenter(target);
      editor.setPosition({lineNumber: target, column: 1});
      try { editor.focus(); } catch { /* not focusable (hidden) */ }
      // Centred again once the editor has its size: revealed before its first layout, the line ended at the top on
      // some loads. Next frames, and the first layout change within ~1.5 s.
      const centre = () => { try { editor.revealLineInCenter(target); } catch { /* disposed */ } };
      if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(() => requestAnimationFrame(centre)); }
      let layoutSubscription = null;
      try { layoutSubscription = editor.onDidLayoutChange(() => { centre(); layoutSubscription?.dispose?.(); }); } catch { layoutSubscription = null; }
      setTimeout(() => { try { layoutSubscription?.dispose?.(); } catch { /* disposed */ } }, 1500);
      try {
        const ids = editor.deltaDecorations(lineFlashRef.current, [{
          range: new monaco.Range(target, 1, target, 1),
          options: {isWholeLine: true, className: 'monaco-line-flash', linesDecorationsClassName: 'monaco-line-flash-gutter'},
        }]);
        lineFlashRef.current = ids;
        setTimeout(() => {
          try { lineFlashRef.current = editor.deltaDecorations(ids, []); } catch { lineFlashRef.current = []; }
        }, 2000);
      } catch { /* the highlight is a nicety */ }
      lastScrolledLineRef.current = lineKey;
    };
    reveal();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [file?.lineNumber, file?.path, editorMounted, value]);

  // Clear editor state when switching services
  // Use ref to track previous values and only reset when they ACTUALLY change
  const prevProjectServiceRef = useRef({ projectId: null, serviceName: null });
  
  useEffect(() => {
    const prev = prevProjectServiceRef.current;
    
    // Only reset if the values have ACTUALLY changed (not just re-renders)
    const hasProjectChanged = prev.projectId !== null && prev.projectId !== projectId;
    const hasServiceChanged = prev.serviceName !== null && prev.serviceName !== serviceName;
    
    if ((hasProjectChanged || hasServiceChanged) && projectId && serviceName) {
      // eslint-disable-next-line no-console
      devLog('🔄 Service changed in MonacoFileEditor, refreshing state:', serviceName);
      setValue('');
      setModified(false);
      setGitState(null);
      setPreviewState(null);
      setIsAIEditing(false);
      setErrorCount(0);
      setWarningCount(0);
    }
    
    // Update ref with current values
    prevProjectServiceRef.current = { projectId, serviceName };
  }, [projectId, serviceName]);

  // Listen for git pull events and refresh content
  useEffect(() => {
    const handleGitPull = async (event) => {
      const {projectId: pulledProjectId, serviceName: pulledServiceName} = event.detail;
      
      // Only refresh if this editor's project/service was pulled
      if (projectId === pulledProjectId && serviceName === pulledServiceName && file?.path) {
        // eslint-disable-next-line no-console
        devLog('🔄 Git pull detected, refreshing editor content for:', file.path);
        
        // Get the fresh content from IndexedDB (git pull already saved it there)
        const freshFile = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);

        if (freshFile && freshFile.content === valueRef.current) {
          // Already showing this exact content (a duplicate broadcast, or
          // this tab's own edit echoing back) — skip setFileContent's
          // model.setValue(), which would otherwise reset cursor position/
          // undo history for no visible change.
          return;
        }

        if (freshFile && freshFile.content) {
          // Update editor with fresh content from remote. Flagged so
          // handleChange (fired synchronously by setFileContent's
          // model.setValue()) knows this wasn't a real keystroke — see
          // isApplyingExternalUpdateRef's own comment.
          isApplyingExternalUpdateRef.current = true;
          setFileContent(freshFile.content);
          isApplyingExternalUpdateRef.current = false;
          setModified(false);
          setGitState(GIT_STATE.ORIGINAL); // Mark as synced with remote
          setPreviewState(PREVIEW_STATE.NOT_SENT);

          // eslint-disable-next-line no-console
          devLog('✅ Editor content refreshed with latest from GitLab');
        } else {
          // File might have been deleted from remote, clear editor
          // eslint-disable-next-line no-console
          console.warn('⚠️ File no longer exists after git pull:', file.path);
        }
      }
    };

    window.addEventListener('gitPullCompleted', handleGitPull);
    
    return () => {
      window.removeEventListener('gitPullCompleted', handleGitPull);
    };
  }, [file, projectId, serviceName]);

  // Handle ESC key to exit fullscreen or close search panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showSearchPanel) {
          setShowSearchPanel(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, showSearchPanel]);

  // Listen for external content update events (from AI)
  // OPTIMIZATION: Parallel processing - different files process concurrently,
  // same file updates are sequenced to maintain consistency
  useEffect(() => {
    const handleAIContentUpdate = (event) => {
      // Make handler non-async to allow parallel processing
      // Wrap async work in promise that executes without blocking
      const processUpdate = async () => {
        const {filePath, content, updateType, oldString, newString, isStreaming, diffContent, projectId: eventProjectId, serviceName: eventServiceName} = event.detail;
        
        try {
          // CRITICAL: For unified diffs, we need to parse them first to check for file creation/modification
          // File operations (create/delete/modify) must be processed even if no file is open
          if (updateType === 'diff' && diffContent) {
            // Check if this is a file creation diff (--- /dev/null or --- a//dev/null)
            const isFileCreation = /^---\s+(?:a\/)?\/dev\/null$/m.test(diffContent);
            // Check if this is a file deletion diff (+++ /dev/null or +++ b//dev/null)
            const isFileDeletion = /^\+\+\+\s+(?:b\/)?\/dev\/null$/m.test(diffContent);
            
            if (isFileCreation || isFileDeletion) {
              // eslint-disable-next-line no-console
              devLog(`🆕 Detected file ${isFileCreation ? 'creation' : 'deletion'} diff, using external parser`);
              
              // Use external parser to handle file creation/deletion
              // Extract target path using external parser
              const parsed = parseUnifiedDiff(diffContent);
              if (parsed.isValid) {
                if (parsed.operation === 'create') {
                  const result = await applyUnifiedDiff(diffContent, '');
                  if (result.success) {
                    window.dispatchEvent(new CustomEvent('fileOperation', {
                      detail: {
                        operation: 'create',
                        path: result.targetPath,
                        content: result.newContent,
                        rawDiff: diffContent
                      }
                    }));
                  } else {
                    // eslint-disable-next-line no-console
                    console.error(`❌ Failed to create file ${parsed.targetPath}:`, result.error);
                    
                    // Dispatch diffApplicationFailed for AI notification
                    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                      detail: {
                        projectId: projectId || eventProjectId,
                        serviceName: serviceName || eventServiceName,
                        filePath: parsed.targetPath,
                        fileName: parsed.targetPath.split('/').pop(),
                        currentContent: '',
                        currentLineCount: 0,
                        failedDiff: diffContent,
                        error: result.error,
                        isFileCreation: true
                      }
                    }));
                  }
                } else if (parsed.operation === 'delete') {
                  window.dispatchEvent(new CustomEvent('fileOperation', {
                    detail: {
                      operation: 'delete',
                      path: parsed.targetPath
                    }
                  }));
                }
              }
              
              // Don't process further - file operation handled
              return;
            }
            
            // For file modifications, check if this diff is for a different file
            // If so, we should still process it by saving to localStorage
            if (file && filePath && file.path !== filePath && file.name !== filePath) {
              // eslint-disable-next-line no-console
              devLog(`📝 Diff is for different file (${filePath}), current file is (${file.path}). Will apply in background.`);
              
              // Apply diff in background without opening the file
              applyDiffInBackground(diffContent, filePath, eventProjectId, eventServiceName);
              
              // Don't process further - background application handles it
              return;
            }
          }
          
          // Only update if this is the file being edited
          // More flexible path matching to handle various path formats
          const isCurrentFile = file && filePath && (
            file.path === filePath || 
            file.name === filePath ||
            file.path.endsWith(filePath) ||
            filePath.endsWith(file.name)
          );
          
          // eslint-disable-next-line no-console
          devLog(`🔍 MonacoFileEditor: File matching check for aiContentUpdate:`, {
            hasFile: !!file,
            eventFilePath: filePath,
            'file.path': file?.path,
            'file.name': file?.name,
            isCurrentFile,
            updateType,
            hasDiffContent: !!diffContent,
            eventProjectId,
            eventServiceName,
            currentProjectId: projectId,
            currentServiceName: serviceName
          });
          
          if (isCurrentFile) {
        setIsAIEditing(isStreaming);
        
        let finalContent = content;
        let modifiedLines = [];
        
        // Check for unified diff format first (preferred method)
        if (updateType === 'diff' && diffContent) {
          // eslint-disable-next-line no-console
          devLog('📊 Processing unified diff from AI - loading from IndexedDB');
          
          try {
            // Get current content from IndexedDB first (source of truth), then fall back to state
            let currentContent = value;
            
            if (projectId && serviceName && file?.path) {
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
                // eslint-disable-next-line no-console
                devLog('📂 Using content from IndexedDB for diff base');
              }
            }
            
            // CRITICAL: Validate that we have the correct content before applying diff
            // eslint-disable-next-line no-console
            devLog('🔍 Validating current content before applying diff');
            // eslint-disable-next-line no-console
            devLog('   File:', file?.path);
            // eslint-disable-next-line no-console
            devLog('   Current file lines:', currentContent.split('\n').length);
            // eslint-disable-next-line no-console
            devLog('   Content preview (first 150 chars):', currentContent.substring(0, 150));
            
            // Check if content looks invalid (e.g., contains installation instructions instead of code)
            const looksLikeInstallInstructions = currentContent.includes('npm install') || currentContent.includes('yarn add');
            const looksLikeCode = currentContent.includes('import') || currentContent.includes('function') || currentContent.includes('const');
            
            if (looksLikeInstallInstructions && !looksLikeCode && file?.path) {
              // eslint-disable-next-line no-console
              console.warn('⚠️ DETECTED: Editor contains invalid content (installation instructions)');
              // eslint-disable-next-line no-console
              console.warn('   Attempting to reload correct content from localStorage or server...');
              
              // Try to get correct content from IndexedDB first
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored && stored.content && stored.content !== currentContent) {
                // eslint-disable-next-line no-console
                devLog('✅ Loaded correct content from IndexedDB');
                currentContent = stored.content;
              } else if (file.content && file.content !== currentContent) {
                // eslint-disable-next-line no-console
                devLog('✅ Using file.content from props');
                currentContent = file.content;
              } else {
                // eslint-disable-next-line no-console
                console.error('❌ Could not find valid content - diff may fail');
              }
            }
            
            const result = await applyUnifiedDiff(diffContent, currentContent);
            
            // CRITICAL: Check if this is a multi-file diff
            if (result.isMultiFile && result.files && result.files.length > 0) {
              // eslint-disable-next-line no-console
              devLog(`🗂️  Multi-file diff detected with ${result.files.length} files - processing each separately`);
              
              // Process each file in the multi-file diff
              for (const fileInfo of result.files) {
                // eslint-disable-next-line no-console
                devLog(`📝 Processing ${fileInfo.operation} for ${fileInfo.targetPath}`);
                
                // Handle file deletion
                if (fileInfo.isFileDeletion) {
                  // eslint-disable-next-line no-console
                  devLog(`🗑️ Deleting file from IndexedDB: ${fileInfo.targetPath}`);
                  
                  if (projectId && serviceName) {
                    await indexedDbFileStorage.deleteFile(projectId, serviceName, fileInfo.targetPath);
                    
                    // Show success notification
                    window.dispatchEvent(new CustomEvent('showNotification', {
                      detail: {
                        type: 'success',
                        message: `File deleted: ${fileInfo.targetPath.split('/').pop()}`,
                        duration: 3000
                      }
                    }));
                    
                    // eslint-disable-next-line no-console
                    devLog(`✅ File deleted from IndexedDB: ${fileInfo.targetPath}`);
                  }
                  continue; // Skip to next file
                }
                
                // Check if this is the currently open file
                const isCurrentFile = file && (file.path === fileInfo.targetPath || file.name === fileInfo.targetPath);
                
                if (isCurrentFile) {
                  // Apply diff to current file in the editor
                  // eslint-disable-next-line no-console
                  devLog(`📂 Applying diff to currently open file: ${fileInfo.targetPath}`);
                  
                  // Recursively call applyUnifiedDiff for single file
                  const singleFileResult = await applyUnifiedDiff(fileInfo.diffContent, currentContent);
                  
                  if (singleFileResult.success && singleFileResult.newContent) {
                    setValue(singleFileResult.newContent);
                    setModified(true);
                    
                    if (projectId && serviceName && file?.path) {
                      // Preserve stored originalContent from IndexedDB
                      const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                      const originalContentToSave = stored?.originalContent || file?.content || '';
                      
                      await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, singleFileResult.newContent, {
                        originalContent: originalContentToSave,
                        fileName: file.name, ...liveSave()
                      });
                      
                      const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                      setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);

                      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                        detail: {projectId, serviceName, filePath: file.path}
                      }));
                      
                      // eslint-disable-next-line no-console
                      devLog('💾 Multi-file diff - current file auto-saved to IndexedDB:', file.path);
                    }
                  } else {
                    // eslint-disable-next-line no-console
                    console.error(`❌ Failed to apply diff to current file ${fileInfo.targetPath}:`, singleFileResult.error);
                  }
                } else {
                  // Apply diff to other file in background
                  // eslint-disable-next-line no-console
                  devLog(`📝 Applying diff in background for: ${fileInfo.targetPath}`);
                  applyDiffInBackground(fileInfo.diffContent, fileInfo.targetPath, eventProjectId, eventServiceName);
                }
              }
              
              // All files processed
              return;
            }
            
            // Single file diff - process normally
            if (result.success && result.newContent) {
              // eslint-disable-next-line no-console
              devLog(`✅ Diff applied successfully to ${file.path}`);
              
              // Save to IndexedDB immediately (source of truth)
              if (projectId && serviceName && file?.path) {
                // Update state after successful save
                setValue(result.newContent);
                setModified(true);
                
                // Preserve stored originalContent from IndexedDB
                const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                const originalContentToSave = stored?.originalContent || file?.content || '';
                
                await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, result.newContent, {
                  originalContent: originalContentToSave,
                  fileName: file.name, ...liveSave()
                });
                
                const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                setGitState(currentState?.gitState || null);
                setPreviewState(currentState?.previewState || null);

                // eslint-disable-next-line no-console
                devLog(`📢 Dispatching monacoFileChanged event for ${file.path} to update tree view, editor, and preview`);
                window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                  detail: {projectId, serviceName, filePath: file.path}
                }));
                
                // eslint-disable-next-line no-console
                devLog('💾 Changes auto-saved to IndexedDB:', file.path);
              }
              
              return; // Exit early for diff handling
            } else {
              // eslint-disable-next-line no-console
              console.error('❌ Failed to apply diff:', result.error);
              // eslint-disable-next-line no-console
              console.error('💡 This usually means the diff is based on a different version of the file.');
              // eslint-disable-next-line no-console
              console.error('   Current file has', currentContent.split('\n').length, 'lines');
              // eslint-disable-next-line no-console
              console.error('💡 Dispatching event to request AI regenerate with correct content.');
              
              // Dispatch event to notify that diff failed and AI needs correct content
              window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                detail: {
                  projectId,
                  serviceName,
                  filePath: file?.path,
                  fileName: file?.name,
                  currentContent: currentContent,
                  currentLineCount: currentContent.split('\n').length,
                  failedDiff: diffContent,
                  error: result.error
                }
              }));
              
              // Don't apply corrupted content - keep current content
              return;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse/apply unified diff:', error);
            // Fall through to regular content update
          }
        }
        
        // Check if content looks like a diff but wasn't explicitly marked as one
        if (!isStreaming && content && eventProjectId === projectId && eventServiceName === serviceName) {
          // Check if content contains unified diff markers
          if (/^---\s+/m.test(content) && /^\+\+\+\s+/m.test(content)) {
            // eslint-disable-next-line no-console
            devLog('📄 Content appears to be a unified diff, processing with external parser');
            
            // Get current content from IndexedDB first (source of truth)
            let currentContent = value;
            
            if (projectId && serviceName && file?.path) {
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
              }
            }
            
            // Validate content before applying diff
            const looksLikeInstallInstructions = currentContent.includes('npm install') || currentContent.includes('yarn add');
            const looksLikeCode = currentContent.includes('import') || currentContent.includes('function') || currentContent.includes('const');
            
            if (looksLikeInstallInstructions && !looksLikeCode && file?.path) {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Invalid content detected, reloading from storage');
              const stored = indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
                setValue(stored.content);
              } else if (file?.content) {
                currentContent = file.content;
                setValue(file.content);
              }
            }
            
            const result = await applyUnifiedDiff(content, currentContent);
            
            // Check if this is a multi-file diff
            if (result.isMultiFile && result.files && result.files.length > 0) {
              // eslint-disable-next-line no-console
              devLog(`🗂️  Auto-detected multi-file diff with ${result.files.length} files`);
              
              // Process each file
              for (const fileInfo of result.files) {
                const isCurrentFile = file && (file.path === fileInfo.targetPath || file.name === fileInfo.targetPath);
                
                if (isCurrentFile) {
                  const singleFileResult = await applyUnifiedDiff(fileInfo.diffContent, currentContent);
                  
                  if (singleFileResult.success && singleFileResult.newContent) {
                    setValue(singleFileResult.newContent);
                    setModified(true);
                    
                    if (projectId && serviceName && file?.path) {
                      // Preserve stored originalContent from IndexedDB
                      const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                      const originalContentToSave = stored?.originalContent || file?.content || '';
                      
                      indexedDbFileStorage.saveFile(projectId, serviceName, file.path, singleFileResult.newContent, {
                        originalContent: originalContentToSave,
                        fileName: file.name, ...liveSave()
                      });
                      
                      const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                      setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
                      
                      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                        detail: {projectId, serviceName, filePath: file.path}
                      }));
                      
                      // eslint-disable-next-line no-console
                      devLog('💾 Multi-file diff - current file auto-saved:', file.path);
                    }
                  }
                } else {
                  // Apply other files in background
                  applyDiffInBackground(fileInfo.diffContent, fileInfo.targetPath, eventProjectId, eventServiceName);
                }
              }
              
              return; // Exit early
            }
            
            // Single file diff
            if (result.success && result.newContent) {
              setValue(result.newContent);
              setModified(true);
              
              // Save to localStorage
              if (projectId && serviceName && file?.path) {
                // Preserve stored originalContent from IndexedDB
                const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                const originalContentToSave = stored?.originalContent || file?.content || '';
                
                indexedDbFileStorage.saveFile(projectId, serviceName, file.path, result.newContent, {
                  originalContent: originalContentToSave,
                  fileName: file.name, ...liveSave()
                });
                
                const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
                
                window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                  detail: {projectId, serviceName, filePath: file.path}
                }));
                
                // eslint-disable-next-line no-console
                devLog('💾 Diff auto-saved to localStorage:', file.path);
              }
              
              return; // Exit early for diff
            } else {
              // eslint-disable-next-line no-console
              console.error('❌ Failed to apply diff:', result.error);
              // eslint-disable-next-line no-console  
              console.error('💡 Dispatching event to request AI regenerate with correct content.');
              
              // Dispatch event to notify that diff failed and AI needs correct content
              window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                detail: {
                  projectId,
                  serviceName,
                  filePath: file?.path,
                  fileName: file?.name,
                  currentContent: currentContent,
                  failedDiff: content,
                  error: result.error
                }
              }));
              
              // Don't apply corrupted content
              return;
            }
          }
          
          // Not a diff - apply as full content replacement
          // eslint-disable-next-line no-console
          devLog('📄 Full file update (non-diff content)');
          
          setValue(content);
          setModified(true);
          finalContent = content;
          
          // Save to localStorage
          if (projectId && serviceName && file?.path) {
            // Preserve stored originalContent from IndexedDB
            const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            const originalContentToSave = stored?.originalContent || file?.content || '';
            
            indexedDbFileStorage.saveFile(projectId, serviceName, file.path, content, {
              originalContent: originalContentToSave,
              fileName: file.name, ...liveSave()
            });
            
            const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
            setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
            
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {projectId, serviceName, filePath: file.path}
            }));
            
            // eslint-disable-next-line no-console
            devLog('💾 Full file update saved to localStorage:', file.path);
          }
        
        // Handle streaming or partial updates (replace operations)
        if (updateType === 'replace' && oldString && newString && editorRef.current) {
            const currentContent = editorRef.current.getValue();
            
            if (currentContent.includes(oldString)) {
              // Perform the replacement
              finalContent = currentContent.replace(oldString, newString);
              
              // Calculate which lines were modified for highlighting
              const oldStringLines = oldString.split('\n');
              const newStringLines = newString.split('\n');
              
              // Find the line where replacement occurred
              const replacementIndex = currentContent.indexOf(oldString);
              const linesBefore = currentContent.substring(0, replacementIndex).split('\n');
              const startLine = linesBefore.length;
              const endLine = startLine + newStringLines.length - 1;
              
              // Track modified lines for highlighting
              for (let i = startLine; i <= endLine; i++) {
                modifiedLines.push(i);
              }
              
              // eslint-disable-next-line no-console
              devLog(`🔧 Partial update: replaced ${oldStringLines.length} line(s) with ${newStringLines.length} line(s)`);
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Could not find oldString in current content, applying as full update');
              finalContent = content || currentContent;
            }
          }
          
          if (updateType === 'replace' || isStreaming) {
            setValue(finalContent);
          
            // Update the editor content if editor is mounted
            if (editorRef.current && monacoRef.current) {
              const currentPosition = editorRef.current.getPosition();
              const model = editorRef.current.getModel();
              
              // Use pushEditOperations to preserve undo/redo stack
              // This is CRITICAL - setValue() clears the undo stack!
              if (model) {
                const fullRange = model.getFullModelRange();
                editorRef.current.executeEdits('ai-update', [{
                  range: fullRange,
                  text: finalContent,
                  forceMoveMarkers: true
                }]);
                
                // Restore cursor position if not streaming (final update)
                if (!isStreaming && currentPosition) {
                  editorRef.current.setPosition(currentPosition);
                }
              }
              
              // Highlight modified lines
              if (modifiedLines.length > 0 && !isStreaming) {
                const decorations = modifiedLines.map(lineNumber => ({
                  range: new monacoRef.current.Range(lineNumber, 1, lineNumber, 1),
                  options: {
                    isWholeLine: true,
                    className: 'ai-modified-line',
                    glyphMarginClassName: 'ai-modified-glyph'
                  }
                }));
                
                // Apply decorations and remove them after 3 seconds
                const decorationIds = editorRef.current.deltaDecorations([], decorations);
                setTimeout(() => {
                  if (editorRef.current) {
                    editorRef.current.deltaDecorations(decorationIds, []);
                  }
                }, 3000);
                
                // eslint-disable-next-line no-console
                devLog(`✨ Highlighted ${modifiedLines.length} modified line(s)`);
              }
            }
          }
        }
        
        // CRITICAL: Save to IndexedDB when AI updates content
        if (projectId && serviceName && file?.path && !isStreaming) {
          if (finalContent) {
            setModified(true);
            
            // Preserve stored originalContent from IndexedDB
            const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            const originalContentToSave = stored?.originalContent || file?.content || '';
            
            indexedDbFileStorage.saveFile(projectId, serviceName, file.path, finalContent, {
              originalContent: originalContentToSave,
              fileName: file.name, ...liveSave()
            });
            // Update file state
            const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
            setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
            
            // Dispatch custom event for auto-sync
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {projectId, serviceName, filePath: file.path}
            }));
            
            // eslint-disable-next-line no-console
            devLog('💾 AI content saved to localStorage:', file.path);
          }
        }
        
        // Notify parent of content change
        if (onContentChange) {
          onContentChange({
            ...file,
            content: finalContent
          });
        }
      }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error in processUpdate:', error);
      }
    };
      
      // Execute async work without blocking (allows parallel processing)
      processUpdate().catch(error => {
        // eslint-disable-next-line no-console
        console.error('❌ Error processing AI content update:', error);
      });
    };

    window.addEventListener('aiContentUpdate', handleAIContentUpdate);
    
    // Listen for file operation events (create, delete, rename)
    const handleFileOperation = (event) => {
      const {operation, path, oldPath, newPath} = event.detail;
      
      // eslint-disable-next-line no-console
      devLog(`📁 File operation: ${operation}`, {path, oldPath, newPath});
      
      // These operations are handled by parent components
      // This is just for logging and potential UI updates
      if (operation === 'create') {
        // eslint-disable-next-line no-console
        devLog(`✨ Creating new file: ${path}`);
      } else if (operation === 'delete') {
        // eslint-disable-next-line no-console
        devLog(`🗑️ Deleting file: ${path}`);
      } else if (operation === 'rename') {
        // eslint-disable-next-line no-console
        devLog(`📝 Renaming file: ${oldPath} → ${newPath}`);
      }
    };
    
    window.addEventListener('fileOperation', handleFileOperation);
    
    return () => {
      window.removeEventListener('aiContentUpdate', handleAIContentUpdate);
      window.removeEventListener('fileOperation', handleFileOperation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, onContentChange, projectId, serviceName]);

  const handleEditorBeforeMount = (monaco) => {
    // Store monaco reference early (idempotent — safe to reassign the same
    // global monaco instance on every file open).
    monacoRef.current = monaco;

    // NOTE: this function used to bail out early via `if (monacoRef.current)
    // return`, on the assumption that re-running it would be "duplicate
    // initialization." But this function does nothing except call the
    // idempotent global setCompilerOptions/setDiagnosticsOptions/
    // setEagerModelSync setters below — there's no provider registration
    // here that could actually double-register. That early return meant
    // isReactFile/isNodeFile (computed from `file?.name` right below) were
    // only ever evaluated for whichever file happened to be open FIRST in a
    // given editor session, and every file opened afterwards silently kept
    // running under that first file's compiler options — e.g. a .jsx file
    // opened after any non-.jsx file would have no `jsx: JsxEmit.ReactJSX`
    // and no DOM lib, causing TypeScript to misparse valid JSX as a type
    // assertion and report real-looking but entirely false syntax errors.
    // Re-running this on every file open (React re-renders when `file`
    // changes anyway) fixes that at zero cost.

    // Determine ECMAScript target based on file type
    // React files (.jsx, .tsx) use ES2022
    // Node.js files (server-side .js, .ts, .mjs, .cjs) use ES2023
    const isReactFile = file?.name && /\.(jsx|tsx)$/i.test(file.name);
    const isNodeFile = file?.name && /\.(js|ts|mjs|cjs)$/i.test(file.name) && !isReactFile;
    
    // Select appropriate ES target:
    // - ES2022 for React files (supports all React 18 features)
    // - ES2023 for Node.js files (supports latest Node.js LTS features)
    // - ESLatest as fallback for other files
    let scriptTarget = monaco.languages.typescript.ScriptTarget.ESLatest;
    if (isReactFile) {
      scriptTarget = monaco.languages.typescript.ScriptTarget.ES2022;
    } else if (isNodeFile) {
      // Note: Monaco might not have ES2023 enum value, use ESLatest for ES2023 support
      scriptTarget = monaco.languages.typescript.ScriptTarget.ES2022; // Will update if ES2023 becomes available
    }
    
   // Configure TypeScript compiler options
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    target: scriptTarget,
    allowNonTsExtensions: true,
    // FIX: moduleResolution must be NodeJs or Bundler, not ReactJSX
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs, 
    module: monaco.languages.typescript.ModuleKind.ESNext,
    noEmit: true,
    esModuleInterop: true,
    // This is the correct place for ReactJSX
    jsx: monaco.languages.typescript.JsxEmit.ReactJSX, 
    allowJs: true,
    allowSyntheticDefaultImports: true,
    resolveJsonModule: true,
    typeRoots: ['node_modules/@types'],
    // Add lib support for ES2022 and ES2023 features
    lib: isReactFile 
      ? ['ES2022', 'DOM', 'DOM.Iterable'] 
      : isNodeFile 
        ? ['ES2023'] 
        : ['ESNext', 'DOM', 'DOM.Iterable'],
    // Relax type checking for better dev experience
    skipLibCheck: true,
    noImplicitAny: false,
    strict: false
  });

  // Sync JavaScript defaults as well
  monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
    monaco.languages.typescript.typescriptDefaults.getCompilerOptions()
  );

    // Enable diagnostics and semantic highlighting but suppress module resolution errors
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      // Suppress common import/module errors in browser environment
      diagnosticCodesToIgnore: [
        2304, // Cannot find name (e.g., 'React')
        2305, // Module has no exported member
        2307, // Cannot find module or its corresponding type declarations
        2318, // Cannot find global type 'ImportMeta'
        2322, // Type is not assignable to type
        2339, // Property does not exist on type
        2345, // Argument of type is not assignable to parameter of type
        2503, // Cannot find namespace 'React' (React.ReactNode etc. — Monaco's standalone
              // TS worker has no @types/react on its module path, unlike the real build)
        2552, // Cannot find name. Did you mean...?
        2580, // Cannot find name 'console'
        2581, // Cannot find name 'window'
        2582, // Cannot find name 'navigator'
        2583, // Cannot find name 'Set'. Do you need to change your target library?
        2584, // Cannot find name 'document'. Do you need to change your target library?
        2585, // Cannot find name 'localStorage'
        2586, // Cannot find name 'sessionStorage'
        2587, // Cannot find name 'fetch'
        2588, // Cannot assign to read-only property
        2589, // Cannot find name 'Element'
        2697, // Async functions are only available when targeting ECMAScript 2015 or higher
        2711, // Dynamic import call returns a 'Promise' (needs ES2015 lib or Promise declaration)
        2769, // No overload matches this call
        7016, // Could not find a declaration file for module
        2875, // JSX tag requires module path 'react/jsx-runtime' (modern JSX transform)
      ]
    });

    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      // Suppress common import/module errors in browser environment
      diagnosticCodesToIgnore: [
        2304, // Cannot find name (e.g., 'React')
        2305, // Module has no exported member
        2307, // Cannot find module or its corresponding type declarations
        2318, // Cannot find global type 'ImportMeta'
        2322, // Type is not assignable to type
        2339, // Property does not exist on type
        2345, // Argument of type is not assignable to parameter of type
        2503, // Cannot find namespace 'React' (React.ReactNode etc.)
        2552, // Cannot find name. Did you mean...?
        2580, // Cannot find name 'console'
        2581, // Cannot find name 'window'
        2582, // Cannot find name 'navigator'
        2583, // Cannot find name 'Set'. Do you need to change your target library?
        2584, // Cannot find name 'document'. Do you need to change your target library?
        2585, // Cannot find name 'localStorage'
        2586, // Cannot find name 'sessionStorage'
        2587, // Cannot find name 'fetch'
        2588, // Cannot assign to read-only property
        2589, // Cannot find name 'Element'
        2697, // Async functions are only available when targeting ECMAScript 2015 or higher
        2711, // Dynamic import call returns a 'Promise' (needs ES2015 lib or Promise declaration)
        2769, // No overload matches this call
        7016, // Could not find a declaration file for module
        2875  // JSX tag requires module path 'react/jsx-runtime' (modern JSX transform)
      ]
    });

    // CRITICAL: Enable eager model sync for better syntax highlighting
    monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true);
    monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true);
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    setEditorMounted((n) => n + 1);
    
    // Don't overwrite monacoRef if already set in beforeMount
    if (!monacoRef.current) {
      monacoRef.current = monaco;
    }
    
    // Ensure the model has the correct language and URI for TSX/JSX files
    const model = editor.getModel();
    if (model && file?.name) {
      // Create proper URI with file extension for Monaco to recognize file type
      const uri = monaco.Uri.parse(`file:///${file.path || file.name}`);
      
      // If the model URI doesn't match, create a new model with correct URI
      if (model.uri.toString() !== uri.toString()) {
        const newModel = monaco.editor.createModel(model.getValue(), language, uri);
        editor.setModel(newModel);
        model.dispose(); // Dispose old model
      }
    }
    
    // Configure editor options
    editor.updateOptions({
      // Matches ProjectFilesBrowser.jsx's Monaco instance (the embedded
      // Chat-mode Files tab) — kept in sync per explicit user request to
      // align the two editors' font size while leaving this editor's own
      // theme/minimap/etc. untouched. lineHeight scales with fontSize at
      // the same ~1.615 ratio as before (21/13) so line spacing reads the
      // same relative to the smaller text, not just shrunk text in the old
      // spacing.
      fontSize: 12,
      fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace",
      lineHeight: 19,
      renderLineHighlight: 'all',
      scrollBeyondLastLine: false,
      minimap: {
        enabled: true
      },
      folding: true,
      glyphMargin: true, // Enable glyph margin for diff icons
      lineNumbers: 'on',
      renderWhitespace: 'selection',
      scrollbar: {
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10
      },
      bracketPairColorization: {
        enabled: true
      },
      guides: {
        bracketPairs: true,
        indentation: true
      },
      wordWrap: 'off',
      automaticLayout: true,
      'semanticHighlighting.enabled': true // Ensure semantic highlighting is enabled
    });

    // Add keyboard shortcuts
    if (!readOnly) {
      // Cmd+S / Ctrl+S to save
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        if (modified && onSave) {
          handleSave();
        }
      });
    }
    
    // Cmd+Shift+F / Ctrl+Shift+F to open search
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
      setShowSearchPanel(prev => !prev);
    });

    // Register custom hover provider for errors with "Ask AI" button
    const hoverProvider = monaco.languages.registerHoverProvider('*', {
      provideHover: (model, position) => {
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        const marker = markers.find(m => {
          return m.startLineNumber <= position.lineNumber &&
                 m.endLineNumber >= position.lineNumber &&
                 m.startColumn <= position.column &&
                 m.endColumn >= position.column &&
                 m.severity === monaco.MarkerSeverity.Error;
        });

        if (!marker) return null;

        const errorText = marker.message;
        const errorCode = marker.code ? ` (${marker.code})` : '';
        const lineNumber = marker.startLineNumber;
        const column = marker.startColumn;
        
        // Get context (5 lines around error)
        const lineCount = model.getLineCount();
        const startLine = Math.max(1, lineNumber - 2);
        const endLine = Math.min(lineCount, lineNumber + 2);
        const contextLines = [];
        
        for (let i = startLine; i <= endLine; i++) {
          contextLines.push(model.getLineContent(i));
        }
        const codeContext = contextLines.join('\n');

        // Create hover content with Ask AI button
        const hoverContent = [
          { value: `**Error**${errorCode}: ${errorText}` },
          { value: '---' },
          {
            value: `[🤖 Ask AI to Fix This Error](command:askAIToFixError?${encodeURIComponent(JSON.stringify({
              error: errorText,
              code: marker.code,
              line: lineNumber,
              column: column,
              filePath: file?.path || file?.name,
              codeContext: codeContext
            }))})`
          }
        ];

        return {
          range: new monaco.Range(
            marker.startLineNumber,
            marker.startColumn,
            marker.endLineNumber,
            marker.endColumn
          ),
          contents: hoverContent
        };
      }
    });

    // Register command handler for Ask AI button
    const commandDisposable = editor.addCommand(0, (ctx, args) => {
      // This will be triggered by the command link in hover
    });

    // Listen for link clicks in hover
    editor.onMouseDown((e) => {
      devLog('🖱️ Monaco editor mouse down', e.target.type, 'Target:', monaco.editor.MouseTargetType.CONTENT_WIDGET);
      
      if (e.target.type === monaco.editor.MouseTargetType.CONTENT_WIDGET) {
        devLog('✅ Content widget clicked, checking href...', e.event.browserEvent.target);
        const commandMatch = e.event.browserEvent.target?.href?.match(/command:askAIToFixError\?(.+)/);
        devLog('🔍 Command match:', commandMatch);
        
        if (commandMatch) {
          devLog('🎯 Ask AI command detected!');
          e.event.preventDefault();
          e.event.stopPropagation();
          
          try {
            const errorData = JSON.parse(decodeURIComponent(commandMatch[1]));
            devLog('📦 Error data parsed:', errorData);
            
            // Create AI message prompt to fix the error
            const aiPrompt = `I have an error in my code that needs fixing:\n\nFile: ${errorData.filePath}\nLine: ${errorData.line}, Column: ${errorData.column}\nError: ${errorData.error}${errorData.code ? ` (${errorData.code})` : ''}\n\nCode context:\n\`\`\`\n${errorData.codeContext}\n\`\`\`\n\nPlease analyze this error and provide a fix.`;
            
            devLog('🤖 Dispatching sendAIMessage event with prompt:', aiPrompt);
            
            // Dispatch event to send to AI chat
            window.dispatchEvent(new CustomEvent('sendAIMessage', {
              detail: {
                message: aiPrompt,
                context: {
                  filePath: errorData.filePath,
                  errorLine: errorData.line,
                  errorColumn: errorData.column
                }
              }
            }));
            
            devLog('✅ sendAIMessage event dispatched');
            
            // Show notification
            window.dispatchEvent(new CustomEvent('showNotification', {
              detail: {
                type: 'info',
                message: '🤖 Asking AI to analyze and fix the error...',
                duration: 3000
              }
            }));
          } catch (error) {
            console.error('❌ Failed to parse error data:', error);
          }
        }
      }
    });
    
    // Monitor for marker changes (errors/warnings from LSP, linting, etc.)
    const updateMarkerCounts = () => {
      const model = editor.getModel();
      if (!model) return;
      
      const markers = monaco.editor.getModelMarkers({resource: model.uri});
      
      // Count errors and warnings
      const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error).length;
      const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning).length;
      
      setErrorCount(errors);
      setWarningCount(warnings);
      
      // Pass errors to parent component if callback provided
      if (onErrorsChange && file?.path) {
        const errorMarkers = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
        onErrorsChange(file.path, errorMarkers);
      }
    };
    
    // Initial marker count
    updateMarkerCounts();
    
    // Listen for marker changes
    const markersDisposable = monaco.editor.onDidChangeMarkers(() => {
      updateMarkerCounts();
    });
    
    // Cleanup on unmount
    return () => {
      markersDisposable.dispose();
    };
  };

  const handleChange = async (newValue) => {
    // This "change" was us overwriting the buffer with fresh sync content,
    // not the user typing — don't let it queue a save, mark the file
    // modified, or notify collaborators. See isApplyingExternalUpdateRef.
    if (isApplyingExternalUpdateRef.current) {
      return;
    }
    setValue(newValue);
    const isModified = newValue !== (file?.content || '');
    setModified(isModified);
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce expensive operations (IndexedDB save, event dispatch, tree refresh)
    debounceTimerRef.current = setTimeout(async () => {
      // Save to localStorage if projectId and serviceName are provided
      if (projectId && serviceName && file?.path) {
        if (isModified) {
          // Preserve stored originalContent from IndexedDB
          const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
          const originalContentToSave = stored?.originalContent || file?.content || '';
          
          await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, newValue, {
            originalContent: originalContentToSave,
            fileName: file.name, ...liveSave()
          });
          // Update file state
          const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
          setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
          
          // No manual collaborator broadcast needed here: when collaboration
          // is enabled, useCollaboration's MonacoBinding already syncs every
          // keystroke live via vfs-service's Yjs doc — this debounced
          // indexedDbFileStorage.saveFile a few lines up ALSO write-throughs
          // via patchFile, which merges through that same doc (see its own
          // comment), so any other live viewer already sees the change.

          // Dispatch custom event for auto-sync (debounced)
          window.dispatchEvent(new CustomEvent('monacoFileChanged', {
            detail: {projectId, serviceName, filePath: file.path}
          }));
        } else {
          // FIX: Content matches original after undo/manual edit
          // Don't delete the file from IndexedDB - just keep it with original content
          // This preserves the file entry and prevents deletion on undo
          const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
          if (stored) {
            // File exists in IndexedDB - update it with the original content
            await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, newValue, {
              originalContent: stored.originalContent || file?.content || '',
              fileName: file.name, ...liveSave()
            });
          }
          setGitState(null);
          setPreviewState(null);
        }
        
        // Notify parent about modification status change
        if (onContentChange) {
          onContentChange({
            ...file,
            content: newValue,
            modified: isModified
          });
        }
      }
    }, 500); // 500ms debounce - balances responsiveness with performance
  };

  const handleSave = async () => {
    // Not connected: nothing is saved (the editor reads only, its text may be an older copy).
    if (connection.blocked) {
      return;
    }
    if (onSave && file) {
      // `onSave` may be async (e.g. a direct vfs-service write for a
      // no-remote service/brain) — await it so a real failure doesn't
      // silently clear the "modified" flag and drop the file from the
      // IndexedDB cache while the content never actually persisted.
      try {
        await onSave({
          ...file,
          content: value
        });
      } catch (saveError) {
        // eslint-disable-next-line no-console
        console.error('❌ Save failed, keeping local changes:', saveError);
        return;
      }

      setModified(false);

      // Remove from IndexedDB after successful save (with event notification)
      if (projectId && serviceName && file?.path) {
        await indexedDbFileStorage.deleteFile(projectId, serviceName, file.path);
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  const toggleTheme = () => {
    // Update app theme, which will trigger the theme watcher
    const newAppTheme = theme === 'vs-dark' ? 'light' : 'dark';
    localStorage.setItem('theme', newAppTheme);
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('themechange'));
    window.dispatchEvent(new Event('storage'));
  };

  const formatDocument = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument')?.run();
    }
  };

  const isPipelineFile = (filename) => {
    if (!filename) return false;
    const lowerName = filename.toLowerCase();
    
    // Check for specific pipeline files
    const pipelineFiles = [
      '.gitlab-ci.yml',
      'jenkinsfile',
      'azure-pipelines.yml',
      '.harness/'
    ];
    
    // Check if it's a GitHub Actions workflow
    if (lowerName.includes('.github/workflows/') && lowerName.endsWith('.yml')) {
      return true;
    }
    
    // Check for other pipeline files
    return pipelineFiles.some(pattern => lowerName.includes(pattern.toLowerCase()));
  };

  const handlePipelineInsert = async (pipelineData) => {
    const { filename, content, platform } = pipelineData;
    
    // Don't close the editor anymore - auto-save in background
    
    // Check if we're updating the current file or creating a new one
    const isUpdatingCurrent = file && (file.path === filename || file.name === filename);
    
    if (isUpdatingCurrent) {
      // Update current file
      setValue(content);
      setModified(true);
      
      // Save to IndexedDB if we have project context
      if (projectId && serviceName) {
        await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, content, {
          originalContent: file.content || '',
          fileName: file.name, ...liveSave()
        });
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('monacoFileChanged', {
          detail: { projectId, serviceName, filePath: file.path }
        }));
      }
    } else {
      // Create new file event - dispatch to parent or handle locally
      const newFile = {
        path: filename,
        name: filename.split('/').pop(),
        content: content,
        type: 'file'
      };
      
      // Save to IndexedDB if we have project context
      if (projectId && serviceName) {
        await indexedDbFileStorage.saveFile(projectId, serviceName, filename, content, {
          originalContent: '',
          fileName: newFile.name
        });
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('pipelineFileCreated', {
          detail: { projectId, serviceName, file: newFile, platform }
        }));
      }
      
      // Update current editor content
      setValue(content);
      setModified(true);
    }
  };

  const printMarkdownPreview = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // Get the already-rendered preview content (with rendered Mermaid diagrams)
      const previewContent = document.querySelector('.markdown-preview-content');
      const clonedContent = previewContent ? previewContent.cloneNode(true) : null;
      
      // If we have rendered content, use it; otherwise fall back to rendering fresh
      const renderedHtml = clonedContent ? clonedContent.innerHTML : `<pre>${escapeHtml(value)}</pre>`;
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${file.name} - Print Preview</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                font-size: 16px;
                line-height: 1.6;
                max-width: 900px;
                margin: 40px auto;
                padding: 20px;
                color: #333;
              }
              h1, h2, h3, h4, h5, h6 { color: #000; margin-top: 24px; }
              h1 { font-size: 2em; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
              h2 { font-size: 1.5em; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
              h3 { font-size: 1.25em; }
              code { background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-family: 'Monaco', 'Menlo', monospace; }
              pre { background: #f5f5f5; padding: 16px; border-radius: 6px; overflow-x: auto; border: 1px solid #ddd; }
              pre code { background: none; padding: 0; }
              a { color: #0366d6; text-decoration: none; }
              table { border-collapse: collapse; width: 100%; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background: #f5f5f5; font-weight: 600; }
              img { max-width: 100%; height: auto; }
              svg { max-width: 100%; height: auto; }
              @media print {
                body { margin: 0; padding: 20px; }
                svg { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${renderedHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const printHtmlPreview = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(value);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const toggleSearchPanel = () => {
    setShowSearchPanel(prev => !prev);
  };

  const handleSearchFileSelect = async (filePath, lineNumber) => {
    try {
      // If it's a different file, dispatch event to open it
      if (file?.path !== filePath) {
        window.dispatchEvent(new CustomEvent('openFileFromSearch', {
          detail: {
            projectId,
            serviceName,
            filePath,
            lineNumber
          }
        }));
      } else {
        // Same file, just navigate to line
        if (editorRef.current && lineNumber) {
          editorRef.current.revealLineInCenter(lineNumber);
          editorRef.current.setPosition({lineNumber, column: 1});
          editorRef.current.focus();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error opening file from search:', error);
    }
  };

  // Track markers (errors/warnings) for the current file when it changes
  useEffect(() => {
    // Wait for next tick to ensure editor is ready and model is set
    const timer = setTimeout(() => {
      if (!editorRef.current || !monacoRef.current || !file?.path) {
        return;
      }
      
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      const model = editor.getModel();
      
      if (!model) {
        return;
      }
      
      const markers = monaco.editor.getModelMarkers({resource: model.uri});
      
      // Count errors and warnings
      const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error).length;
      const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning).length;
      
      setErrorCount(errors);
      setWarningCount(warnings);
      
      // Pass errors to parent component if callback provided
      if (onErrorsChange) {
        const errorMarkers = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
        onErrorsChange(file.path, errorMarkers);
      }
    }, 100); // Small delay to ensure editor model is updated
    
    return () => clearTimeout(timer);
  }, [file?.path, onErrorsChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (!file) {
    return (
      <div className="monaco-editor-empty">
        <span className="empty-icon">{'📄'}</span>
        <p>{'Select a file from the repository to view its contents'}</p>
      </div>
    );
  }

  const language = detectLanguage(file.name);
  const openCommentCount = collaborationEnabled ? editorCollab.threads.filter((t) => !t.resolved).length : 0;

  return (
    <div className={`monaco-editor-container ${isFullscreen ? 'fullscreen' : ''}${isCompact ? ' is-compact' : ''}`} ref={containerRef}>
      <div className={`monaco-editor-header is-fold-${headerFold}`} ref={headerRef}>
        <div className="monaco-file-info">
          <span aria-hidden className="monaco-file-icon">
            <Icon glow={false} name={getFileIconName(file.name)} size={16} />
          </span>
          {/* Always there, however narrow: the path (only the name when very narrow), cut with an ellipsis. */}
          <Tooltip title={file.path}>
            <span className="monaco-file-path" data-testid="editor-file-name">{headerFold >= 2 ? (file.name || file.path.split('/').pop()) : file.path}</span>
          </Tooltip>
          {collaborationEnabled && headerFold < 2 && <FileAuthorChips path={presenceRelPath} snapshot={collab.presence} />}
          {collaborationEnabled ? (
            // Projects in the sandbox: every change goes into the live doc, which saves it to the project, and previews
            // rebuild from the project. "Sent" / "Changed after sent" (the old send-to-preview flow) mean nothing here;
            // "Modified" only while live editing is unavailable and the REST save is still to come.
            collab.status === 'live' ? <span className="monaco-status-pill monaco-live-indicator is-ok" title="Every change is saved to the project as you type"><span aria-hidden className="monaco-status-dot" />{'Live · saved'}</span>
              : collab.status === 'connecting' ? <span className="monaco-status-pill monaco-live-indicator is-connecting">{'Connecting…'}</span>
                : modified && <span className="monaco-status-pill monaco-modified-indicator is-warn"><span aria-hidden className="monaco-status-dot" />{'Modified'}</span>
          ) : (
            <>
              {modified && <span className="monaco-status-pill monaco-modified-indicator is-warn"><span aria-hidden className="monaco-status-dot" />{'Modified'}</span>}
              {previewState === PREVIEW_STATE.SENT && <span className="monaco-status-pill monaco-sent-indicator is-ok">{'✓ Sent'}</span>}
              {previewState === PREVIEW_STATE.OUTDATED && <span className="monaco-status-pill monaco-changed-after-sent-indicator is-warn"><span aria-hidden className="monaco-status-dot" />{'Changed after sent'}</span>}
            </>
          )}
          {isAIEditing && <span className="monaco-status-pill monaco-ai-editing-indicator is-ai"><Robot aria-hidden size={12} />{'AI Editing…'}</span>}
          {isFullscreen && headerFold === 0 && (
            <Button
              className="monaco-btn monaco-btn-close-fullscreen"
              icon={<X size={14} />}
              onClick={toggleFullscreen}
              size="small"
              title="Exit fullscreen (ESC)"
            >
              {'Exit Fullscreen'}
            </Button>
          )}
        </div>
        
        <div className="monaco-toolbar">
          {(() => {
            // The toolbar's buttons, each with the header width at which it moves into "⋯" (`fold`: 1 below ~600px,
            // 2 below ~420px, null never): Format and Copy (and Save) stay longest, then Blame and Comments.
            const actions = [];
            if (collaborationEnabled) {
              actions.push(
                {key: 'blame', name: 'Blame', text: true, fold: 2, icon: <ClockCounterClockwise size={15} />, onClick: onBlameButton, pressed: editorCollab.blameOn, testId: 'toggle-blame', tooltip: 'Blame: who wrote each line, person or AI agent, and when'},
                {key: 'comments', name: openCommentCount ? `Comments (${openCommentCount} open)` : 'Comments', count: openCommentCount, fold: 2, icon: <ChatCircle size={15} />, onClick: () => editorCollab.setCommentsOpen(!editorCollab.commentsOpen), pressed: editorCollab.commentsOpen, testId: 'toggle-comments', tooltip: 'Comments on this file and its lines (@ to mention), and the suggestions on it'},
              );
            }
            actions.push(
              {key: 'collapse', name: 'Collapse all', fold: 1, group: 'view', icon: <ArrowsInLineVertical size={15} />, onClick: collapseAll, tooltip: 'Collapse all code sections'},
              {key: 'expand', name: 'Expand all', fold: 1, group: 'view', icon: <ArrowsOutLineVertical size={15} />, onClick: expandAll, tooltip: 'Expand all code sections'},
              {key: 'theme', name: theme === 'vs-dark' ? 'Switch to light theme' : 'Switch to dark theme', fold: 1, group: 'view', icon: theme === 'vs-dark' ? <Sun size={15} /> : <Moon size={15} />, onClick: toggleTheme},
              {key: 'fullscreen', name: isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen', fold: 1, group: 'view', icon: isFullscreen ? <CornersIn size={15} /> : <CornersOut size={15} />, onClick: toggleFullscreen, tooltip: isFullscreen ? 'Exit fullscreen (ESC)' : 'Enter fullscreen'},
            );
            const previewToggle = (key, on, setOn) => ({key, name: on ? 'Edit' : 'Preview', text: true, fold: 1, group: 'view', icon: on ? <PencilSimple size={15} /> : <Eye size={15} />, onClick: () => setOn(!on), pressed: on, tooltip: on ? 'Show editor' : 'Show preview'});
            if (language === 'markdown') {
              // Never folded into "⋯": the way between the rendered file and its source stays in sight.
              actions.push({...previewToggle('md-preview', showMarkdownPreview, setShowMarkdownPreview), name: showMarkdownPreview ? 'Source' : 'Preview', pressed: undefined, fold: null, testId: 'toggle-md-preview', tooltip: showMarkdownPreview ? 'Show the source to edit it' : 'Show the rendered preview'});
              if (showMarkdownPreview) { actions.push({key: 'md-print', name: 'Print markdown preview', fold: 1, group: 'view', icon: <Printer size={15} />, onClick: printMarkdownPreview}); }
            }
            if (language === 'html') {
              actions.push(previewToggle('html-preview', showHtmlPreview, setShowHtmlPreview));
              if (showHtmlPreview) { actions.push({key: 'html-print', name: 'Print HTML preview', fold: 1, group: 'view', icon: <Printer size={15} />, onClick: printHtmlPreview}); }
            }
            if (file?.name?.endsWith('.svg')) { actions.push(previewToggle('svg-preview', showSvgPreview, setShowSvgPreview)); }
            // Pipeline Editor - for YAML files or when explicitly pipeline files
            if (language === 'yaml' || isPipelineFile(file.name)) {
              actions.push({key: 'pipeline', name: showPipelineEditor ? 'Edit' : 'Pipeline', text: true, fold: 1, group: 'view', icon: showPipelineEditor ? <PencilSimple size={15} /> : <Wrench size={15} />, onClick: () => setShowPipelineEditor(!showPipelineEditor), pressed: showPipelineEditor, tooltip: showPipelineEditor ? 'Close pipeline editor' : 'Create pipeline'});
            }
            actions.push(
              {key: 'format', name: 'Format', text: headerFold < 2, fold: null, group: 'edit', icon: <MagicWand size={15} />, onClick: formatDocument, tooltip: 'Format document'},
              {key: 'copy', name: 'Copy all content', fold: null, group: 'edit', icon: <Copy size={15} />, onClick: handleCopy},
            );
            if (!readOnly && onSave) {
              actions.push({key: 'save', name: 'Save', text: true, fold: null, group: 'edit', primary: true, disabled: !modified, icon: <FloppyDisk size={15} />, onClick: handleSave, tooltip: 'Save file (Cmd+S)'});
            }
            const folded = (action) => action.fold !== null && headerFold >= action.fold;
            const shown = actions.filter((action) => !folded(action));
            const hidden = actions.filter(folded);
            const out = [];
            shown.forEach((action, index) => {
              const previous = shown[index - 1];
              if (previous && (previous.group || 'collab') !== (action.group || 'collab')) {
                out.push(<span aria-hidden className="monaco-toolbar-sep" key={`sep-${action.key}`} />);
              }
              const labelled = action.text || action.count;
              const button = (
                <Button
                  aria-label={action.text ? undefined : action.name}
                  aria-pressed={action.pressed}
                  className={`monaco-btn ${action.pressed ? 'active' : ''} ${action.primary ? 'monaco-btn-primary' : ''}`}
                  data-testid={action.testId}
                  disabled={action.disabled}
                  icon={action.icon}
                  onClick={action.onClick}
                  size="small"
                  type={action.primary ? 'primary' : 'text'}
                >
                  {action.text ? action.name : (action.count || null)}
                </Button>
              );
              out.push(<Tooltip key={action.key} title={action.tooltip || (labelled ? undefined : action.name)}>{button}</Tooltip>);
            });
            if (hidden.length) {
              out.push(
                <Dropdown
                  key="more"
                  menu={{
                    items: hidden.map((action) => ({
                      key: action.key,
                      label: action.name,
                      icon: action.icon,
                      disabled: action.disabled,
                      extra: action.pressed ? <Check aria-label="on" size={13} /> : undefined,
                    })),
                    onClick: ({key}) => hidden.find((action) => action.key === key)?.onClick(),
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button aria-label="More actions" className="monaco-btn" data-testid="editor-more-actions" icon={<DotsThree size={16} weight="bold" />} size="small" type="text" />
                </Dropdown>,
              );
            }
            return out;
          })()}
        </div>
      </div>
      
      {showSearchPanel && (
        <MonacoSearchPanel
          onClose={() => setShowSearchPanel(false)}
          onFileSelect={handleSearchFileSelect}
          projectId={projectId}
          serviceName={serviceName}
        />
      )}
      
      <EditorConnectionBanner connection={connection} />
      {collaborationEnabled && (
        <PresenceEditorBar cursor={collab.myCursor} onSuggest={() => setSuggesting(true)} path={presenceRelPath} projectId={projectId} snapshot={collab.presence} status={connection.blocked ? 'off' : collab.status} viewer={isPresenceViewer} />
      )}
      {collaborationEnabled && (
        <ConflictPanel
          canResolve={!isPresenceViewer}
          conflicts={(collab.presence?.conflicts || []).filter((conflict) => conflict.path === presenceRelPath)}
          projectId={projectId}
          you={collab.presence?.you || null}
        />
      )}
      {collaborationEnabled && isPhone && (
        <BottomSheet onClose={() => editorCollab.setCommentsOpen(false)} open={editorCollab.commentsOpen} testId="comments-sheet" title="Comments">
          <CommentsPanel
            activeThreadId={editorCollab.activeThreadId}
            floating={false}
            line={collab.myCursor?.line ?? editorRef.current?.getPosition()?.lineNumber ?? null}
            onChanged={editorCollab.reloadComments}
            onClose={() => editorCollab.setCommentsOpen(false)}
            onGoToLine={(thread) => { goToCommentLine(thread); editorCollab.setCommentsOpen(false); }}
            focusSuggestionId={focusSuggestionId}
            onGoToSuggestionLine={(line) => { goToLine(line); editorCollab.setCommentsOpen(false); }}
            onScopeChange={setSidePanelScope}
            onSuggest={isPresenceViewer ? () => setSuggesting(true) : null}
            scope={sidePanelScope}
            suggestionsReloadKey={suggestionsReloadKey}
            path={presenceRelPath}
            projectId={projectId}
            sheet
            threads={editorCollab.threads}
            you={{sub: collab.presence?.you?.sub || authUser?.sub, email: authUser?.email}}
          />
        </BottomSheet>
      )}
      {collaborationEnabled && isPhone && (
        <BlameSheet
          blame={editorCollab.blame}
          focusLine={editorCollab.blameLine?.line ?? null}
          onClose={() => setBlameSheetOpen(false)}
          onGoToLine={goToLine}
          onTurnOff={() => { editorCollab.setBlameOn(false); setBlameSheetOpen(false); }}
          open={blameSheetOpen}
          you={collab.presence?.you || null}
        />
      )}
      {collaborationEnabled && suggesting && (
        <SuggestionEditor isOpen onClose={() => setSuggesting(false)} onSubmitted={() => setSuggestionsReloadKey((key) => key + 1)} path={presenceRelPath} projectId={projectId} />
      )}

      <div className={`monaco-editor-wrapper ${showMarkdownPreview && showHtmlPreview ? 'split-preview' : ''}`}>
        {collaborationEnabled && editorCollab.commentsOpen && !isPhone && (
          <CommentsPanel
            activeThreadId={editorCollab.activeThreadId}
            line={collab.myCursor?.line ?? editorRef.current?.getPosition()?.lineNumber ?? null}
            onChanged={editorCollab.reloadComments}
            onClose={() => editorCollab.setCommentsOpen(false)}
            onGoToLine={goToCommentLine}
            focusSuggestionId={focusSuggestionId}
            onGoToSuggestionLine={goToLine}
            onScopeChange={setSidePanelScope}
            onSuggest={isPresenceViewer ? () => setSuggesting(true) : null}
            scope={sidePanelScope}
            suggestionsReloadKey={suggestionsReloadKey}
            path={presenceRelPath}
            projectId={projectId}
            threads={editorCollab.threads}
            you={{sub: collab.presence?.you?.sub || authUser?.sub, email: authUser?.email}}
          />
        )}
        {showPipelineEditor && (
          <PipelineEditor 
            onInsert={handlePipelineInsert}
            currentContent={value}
            filename={file.path || file.name}
          />
        )}
        
        {showMarkdownPreview && language === 'markdown' && (
          <div className="monaco-markdown-preview">
            <MarkdownView
              className="markdown-preview-content"
              filePath={markdownFilePath}
              onOpenFile={openLinkedFile}
              projectId={projectId}
              source={value}
              tableClassName="markdown-table"
              testId="markdown-preview"
            />
          </div>
        )}
        
        {showHtmlPreview && language === 'html' && (
          <div className="monaco-html-preview">
            <iframe
              title="HTML Preview"
              srcDoc={value}
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#ffffff'
              }}
            />
          </div>
        )}
        
        {showSvgPreview && file?.name?.endsWith('.svg') && (
          <div className="monaco-svg-preview" style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
            padding: '20px',
            overflow: 'auto'
          }}>
            <div 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                border: '1px solid rgba(128, 128, 128, 0.3)',
                borderRadius: '4px',
                padding: '20px',
                backgroundColor: '#ffffff'
              }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        )}
        
        {/* PDF preview: the browser's own PDF viewer, fed with the file's bytes */}
        {file?.mimeType === 'application/pdf' && file?.binaryUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '6px 12px', fontSize: 12, backgroundColor: theme === 'vs-dark' ? '#252526' : '#f3f3f3' }}>
              {/* Works even where the page's security policy forbids framing a PDF */}
              <a href={file.binaryUrl} target="_blank" rel="noopener noreferrer">Open in new tab</a>
              <a href={file.binaryUrl} download={file.name}>Download</a>
            </div>
            <iframe
              className="monaco-pdf-preview"
              title={file.name}
              src={file.binaryUrl}
              style={{ flex: 1, width: '100%', border: 0, backgroundColor: theme === 'vs-dark' ? '#1e1e1e' : '#ffffff' }}
            />
          </div>
        )}

        {/* Image preview for binary image files */}
        {(() => {
          const shouldShowImage = !!(file?.isBinary && file?.mimeType?.startsWith('image/') && file?.binaryUrl);
          
          if (shouldShowImage) {
            return (
              <div className="monaco-image-preview" style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
                padding: '20px',
                overflow: 'auto'
              }}>
                <img 
                  src={file.binaryUrl} 
                  alt={file.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    border: '1px solid rgba(128, 128, 128, 0.3)',
                    borderRadius: '4px'
                  }}
                  onLoad={(e) => {
                    devLog('✅ Image loaded successfully:', {
                      naturalWidth: e.target.naturalWidth,
                      naturalHeight: e.target.naturalHeight,
                      src: e.target.src.substring(0, 50)
                    });
                  }}
                  onError={(e) => {
                    console.error('❌ Image failed to load - blob data may be corrupted:', {
                      src: e.target.src.substring(0, 60),
                      naturalWidth: e.target.naturalWidth,
                      naturalHeight: e.target.naturalHeight
                    });
                  }}
                />
              </div>
            );
          }
          return null;
        })()}
        
        {!showPipelineEditor && (!showMarkdownPreview || language !== 'markdown') && (!showHtmlPreview || language !== 'html') && (!showSvgPreview || !file?.name?.endsWith('.svg')) && !(file?.isBinary && file?.mimeType?.startsWith('image/') && file?.binaryUrl) && !(file?.mimeType === 'application/pdf' && file?.binaryUrl) && (
          <Editor
            key={`${file.path}-${language}`}
            beforeMount={handleEditorBeforeMount}
            height="100%"
            language={language}
            loading={
              // The editor's own background for its theme (and full width), so it does not flash a dark box in light
              // mode before the file shows.
              <div className={`monaco-loading${theme === 'vs-dark' ? '' : ' is-light'}`} data-testid="monaco-loading">
                <span>{'Loading file...'}</span>
              </div>
            }
            onChange={handleChange}
            onMount={handleEditorDidMount}
            onValidate={(markers) => {
              // Handle validation markers (errors/warnings)
              // This is called when Monaco's language service detects issues
            }}
            options={{
              readOnly: editorReadOnly || connection.blocked,
              padding: {top: 16, bottom: 16},
              // Disable features that might conflict with React DevTools
              'bracketPairColorization.enabled': true,
              'guides.bracketPairs': true,
              // Formatting options - use 2 spaces
              tabSize: 2,
              insertSpaces: true,
              detectIndentation: false
            }}
            theme={theme}
            value={value}
          />
        )}
      </div>
      
      {file.size && (
        <div className="monaco-editor-footer">
          <div className="monaco-status-left">
            <span className="monaco-status-item">{language}</span>
            <span className="monaco-status-item">
              {`${value.split('\n').length} lines`}
            </span>
            <span className="monaco-status-item">
              {`${(file.size / 1024).toFixed(2)} KB`}
            </span>
          </div>
          <div className="monaco-status-right">
            {errorCount > 0 && (
              <span className="monaco-status-item monaco-error-count" title={`${errorCount} error${errorCount > 1 ? 's' : ''}`}>
                {'❌ '}
                {errorCount}
              </span>
            )}
            {warningCount > 0 && (
              <span className="monaco-status-item monaco-warning-count" title={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}>
                {'⚠️ '}
                {warningCount}
              </span>
            )}
            {errorCount === 0 && warningCount === 0 && (
              <span className="monaco-status-item monaco-no-problems" title="No problems">
                {'✓ No problems'}
              </span>
            )}
            <span className="monaco-status-item">
              {file.encoding || 'UTF-8'}
            </span>
          </div>
        </div>
      )}
      
      {/* Diff Parser Loader */}
      <DiffParserLoader
        fileName={diffLoaderFileName}
        isVisible={showDiffLoader}
        onCancel={() => setShowDiffLoader(false)}
        progressInfo={diffProgressInfo}
      />
      
      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        isOpen={showConflictModal}
        fileName={conflictData?.targetPath || ''}
        error={conflictData?.error}
        onManualResolve={handleManualResolve}
        onCancel={handleConflictCancel}
        failedFiles={failedFiles}
      />
    </div>
  );
};

MonacoFileEditor.propTypes = {
  file: PropTypes.shape({
    content: PropTypes.string,
    encoding: PropTypes.string,
    lineNumber: PropTypes.number,
    name: PropTypes.string,
    path: PropTypes.string,
    size: PropTypes.number
  }),
  isPreviewMode: PropTypes.bool,
  onContentChange: PropTypes.func,
  onErrorsChange: PropTypes.func,
  onSave: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool,
  serviceName: PropTypes.string
};

export default MonacoFileEditor;
